import {
  addUploadedCubeToProjectApi,
  convertCubeApi,
  getCubeInfoByIdApi,
  getMultipartUploadConfigApi,
  initiateMultipartUploadApi,
  multipartUploadApi,
} from '@core/core/api'
import type {
  ConvertProgressNotificationInfo,
  CubeAddedToProjectNotificationInfo,
  LastUploadPartInfo,
  LoadingProgressNotificationInfo,
  MultipartFileStatusPerStepType,
  MultipartUploadedFile,
  SegyHeaderCoordsType,
  SegySetupType,
  UploadedFileStatusType,
  UploadedFileStepTransitionType,
  UploadedFileStepType,
} from '@core/core/types'
import { defaultUploadedFileStepStatuses } from '@core/core/types'
import type { AnyAction, ThunkDispatch } from '@reduxjs/toolkit'
import { nanoid } from '@reduxjs/toolkit'
import { logIfNotTest } from 'common/helpers'
import type { ProjectId } from 'common/types'

import type { AppThunk, RootState } from '../../store'
import { apiWrapper, isMultipartSetupOfSpecificType } from '../helpers'
import {
  selectById,
  selectMultipartFileByCreatedEntityId,
  selectMultipartFileByTaskId,
  selectMultipartFileByUploadId,
  selectMultipartStatus,
} from '../multipartUploadedFileSelectors'
import {
  addMultipartFiles,
  convertStepStatuses,
  DefaultChunkSize,
  getStatus,
  removeMultipartFiles,
  stepsToStatusesMapping,
  transitionToStepsMapping,
  updateMultipartState,
} from './common'

const getChunkNumber = (fileSize: number) => Math.ceil(fileSize / DefaultChunkSize)
const uploadSegyMultipartFiles =
  (projectId: ProjectId, fileId: string, file?: File): AppThunk =>
  async (dispatch, getState) => {
    if (!file) return

    const entity = selectById(getState(), fileId)
    if (!entity) throw new Error('entity не найден')

    const fileSize = file.size
    const chunkNum = getChunkNumber(fileSize)
    let uploadId = entity.uploadId
    const initialChunk = entity.fileChunk
    try {
      if (!initialChunk) {
        if (
          !entity.setupContent ||
          !isMultipartSetupOfSpecificType<SegySetupType>(entity, entity.setupContent, 'segy')
        ) {
          throw new Error('cube params undefined')
        }

        const setupContent = entity.setupContent
        uploadId = await apiWrapper('Вызов initiateMultipartUpload', () => {
          return initiateMultipartUploadApi(file.name, fileSize, projectId, setupContent)
        })

        dispatch(
          updateMultipartState({
            id: fileId,
            changes: { progress: 0, fileChunk: 0, uploadId },
          }),
        )
      }

      if (!uploadId) throw new Error('uploadId undefined')

      for (let i = initialChunk; i < chunkNum; ++i) {
        if (selectMultipartStatus(getState(), fileId, 'loading') === 'paused') {
          return
        }

        const chunkOffset = i * DefaultChunkSize
        const chunkSize = i < chunkNum - 1 ? DefaultChunkSize : fileSize - DefaultChunkSize * i
        const fileHeaderBlob = file.slice(chunkOffset, chunkOffset + chunkSize, file.type)
        const buffer = await fileHeaderBlob.arrayBuffer()

        const chunk = new File([buffer], file.name)
        const uId = uploadId
        await apiWrapper('Вызов multipartUpload', () => {
          return multipartUploadApi(uId, i === chunkNum - 1, chunk)
        })
      }
    } catch (error) {
      logIfNotTest('uploadSegyMultipartFiles', error)
      dispatch(
        updateMultipartState({
          id: fileId,
          changes: {
            statuses: { ...entity.statuses, loading: 'failed' },
            error: error?.toString(),
          },
        }),
      )
    }
  }

export const updateMultipartLoadingState =
  (info: LoadingProgressNotificationInfo): AppThunk =>
  (dispatch, getState) => {
    let entity: MultipartUploadedFile | undefined = undefined
    if (info.entityId) {
      entity = selectMultipartFileByUploadId(getState(), info.entityId)
    } else if (info.taskId) {
      entity = selectMultipartFileByTaskId(getState(), info.taskId)
    }
    if (!entity?.uploadId) return

    const fileId = entity.fileId
    try {
      if (info.progress < 100) {
        dispatch(
          updateMultipartState({
            id: fileId,
            changes: { progress: info.progress, fileChunk: info.partNumber },
          }),
        )
      }
    } catch (error) {
      dispatch(
        updateMultipartState({
          id: fileId,
          changes: {
            statuses: { ...entity.statuses, loading: 'failed' },
            error: error?.toString(),
          },
        }),
      )
    }
  }

export const updateSegyUploadedFileStep = async (
  projectId: ProjectId,
  fileId: string,
  step: UploadedFileStepTransitionType,
  file: File,
  entity: MultipartUploadedFile,
  dispatch: ThunkDispatch<RootState, unknown, AnyAction>,
  state: RootState,
) => {
  if (!entity.statuses) return

  const actualStep: UploadedFileStepType =
    transitionToStepsMapping.get(`${entity.step}->${step}`)?.(entity.statuses, entity.entityType) ??
    'setup'
  const statuses = stepsToStatusesMapping.get(`${entity.step}->${actualStep}`)
  dispatch(
    updateMultipartState({
      id: fileId,
      changes: {
        step: actualStep,
        statuses: { ...entity.statuses, ...statuses },
      },
    }),
  )

  if (
    actualStep === 'loading' &&
    entity.statuses.loading !== 'inprogress' &&
    entity.statuses.loading !== 'succeeded'
  ) {
    dispatch(uploadSegyMultipartFiles(projectId, fileId, file))
  }

  if (
    actualStep === 'conversion' &&
    entity.statuses.conversion !== 'inprogress' &&
    entity.statuses.conversion !== 'succeeded'
  ) {
    await startConversion(dispatch, state, fileId)
  }
}

export const updateUploadedFileStatus =
  (
    projectId: ProjectId,
    fileId: string,
    step: UploadedFileStepType,
    status: UploadedFileStatusType,
    file: File,
  ): AppThunk =>
  (dispatch, getState) => {
    const entity = selectById(getState(), fileId)
    const actualStatus: MultipartFileStatusPerStepType = { [step]: status }
    dispatch(
      updateMultipartState({
        id: fileId,
        changes: {
          step,
          statuses: { ...entity?.statuses, ...actualStatus },
        },
      }),
    )

    if (step === 'loading' && entity?.statuses?.loading === 'paused' && status === 'inprogress') {
      dispatch(uploadSegyMultipartFiles(projectId, fileId, file))
    }
  }

//TODO сделана разбивка функционала по меньшим файлам снизу и до сюда
export const addSegyUncompletedFiles = async (
  dispatch: ThunkDispatch<RootState, unknown, AnyAction>,
  files: LastUploadPartInfo[],
) => {
  for (const file of files) {
    if (!file.fileSize) {
      throw new Error('file undefined')
    }

    const setupContent = file.cubeParams ?? (await getMultipartUploadConfigApi(file.id))
    const headerCoords: SegyHeaderCoordsType = {
      x: { offset: setupContent.headerOffsets.x, type: setupContent.headerTypes.x },
      y: { offset: setupContent.headerOffsets.y, type: setupContent.headerTypes.y },
      iline: { offset: setupContent.headerOffsets.iline, type: setupContent.headerTypes.iline },
      xline: { offset: setupContent.headerOffsets.xline, type: setupContent.headerTypes.xline },
    }

    const convertedSetupContent: SegySetupType = {
      ...setupContent,
      headerCoords,
    }
    convertedSetupContent.isValid = isSetupContentValid(convertedSetupContent)
    const convertedStepStatuses = convertStepStatuses(file.step, file.state)

    dispatch(
      addMultipartFiles([
        {
          projectId: file.projectId,
          fileId: nanoid(),
          name: file.fileName,
          uploadId: file.id,
          progress: (100 * file.partNumber) / (getChunkNumber(file.fileSize) + 1),
          fileSize: file.fileSize,
          fileChunk: file.partNumber,
          ...convertedStepStatuses,
          setupContent: convertedSetupContent,
          initialSetupContent: convertedSetupContent,
          date: -1,
          origin: 'localAlien',
          entityType: 'segy',
        },
      ]),
    )
  }
}

export const updateSegySetupConfig = (
  dispatch: ThunkDispatch<RootState, unknown, AnyAction>,
  fileId: string,
  setupContent: SegySetupType | undefined,
  entity: MultipartUploadedFile | undefined,
) => {
  if (!setupContent) {
    return
  }

  const setupContentWithValid = { ...setupContent, isValid: isSetupContentValid(setupContent) }
  dispatch(
    updateMultipartState({
      id: fileId,
      changes: {
        setupContent: setupContentWithValid,
        statuses: {
          ...defaultUploadedFileStepStatuses,
          setup: setupContentWithValid.isValid ? 'inprogress' : 'failed',
          loading: entity?.statuses?.loading === 'ready' ? 'succeeded' : entity?.statuses?.loading,
          conversion: 'notstarted',
          ready: 'notstarted',
        },
        createdEntity: undefined,
      },
    }),
  )
}

export const updateMultipartConversionState =
  (info: ConvertProgressNotificationInfo): AppThunk =>
  async (dispatch, getState) => {
    const entity = selectMultipartFileByUploadId(getState(), info.entityId)
    if (!entity) return

    const fileId = entity.fileId
    const actualStep = info.status !== 'Done' ? entity.step : 'ready'

    dispatch(
      updateMultipartState({
        id: fileId,
        changes: {
          step: actualStep,
          convertProgress: info.progress,
          statuses: { ...entity.statuses, ...getStatus(info.status) },
        },
      }),
    )

    if (entity.origin === 'crms' && actualStep === 'ready' && entity.uploadId) {
      await addUploadedCubeToProjectApi(entity.projectId, entity.uploadId)
      dispatch(removeMultipartFiles([fileId]))
    }
  }

export const updateSegyReadyState = async (
  dispatch: ThunkDispatch<RootState, unknown, AnyAction>,
  fileId: string,
  entity: MultipartUploadedFile | undefined,
) => {
  if (!entity?.uploadId) return

  const cubeInfo = await getCubeInfoByIdApi(entity.uploadId)
  dispatch(
    updateMultipartState({
      id: fileId,
      changes: {
        createdEntity: cubeInfo,
      },
    }),
  )
}

export const updateStateOnCubeAddedToProject =
  (info: CubeAddedToProjectNotificationInfo): AppThunk =>
  (dispatch, getState) => {
    const createdEntity = selectMultipartFileByCreatedEntityId(getState(), info.cubeId)
    if (!createdEntity) return

    dispatch(removeMultipartFiles([createdEntity.fileId]))
  }

const startConversion = async (
  dispatch: ThunkDispatch<RootState, unknown, AnyAction>,
  state: RootState,
  fileId: string,
) => {
  const entity = selectById(state, fileId)
  if (!entity?.setupContent) {
    throw new Error('cube params undefined')
  }
  try {
    // На беке cubeId это uploadId !
    const uploadId = entity.uploadId ?? ''
    if (isMultipartSetupOfSpecificType<SegySetupType>(entity, entity.setupContent, 'segy')) {
      await convertCubeApi(uploadId, entity.setupContent)
      dispatch(
        updateMultipartState({
          id: fileId,
          changes: {
            convertProgress: 0,
          },
        }),
      )
    }
  } catch (error) {
    dispatch(
      updateMultipartState({
        id: fileId,
        changes: {
          statuses: { ...entity.statuses, conversion: 'failed' },
          error: error?.toString(),
        },
      }),
    )
  }
}

export const addSegyMultipartsToProject = async (
  projectId: ProjectId,
  fileId: string,
  dispatch: ThunkDispatch<RootState, unknown, AnyAction>,
  entity: MultipartUploadedFile,
) => {
  if (entity.uploadId && entity.entityType === 'segy') {
    await addUploadedCubeToProjectApi(projectId, entity.createdEntity?.id ?? '')
    dispatch(removeMultipartFiles([fileId]))
  }
}

const isSetupContentValid = (setupContent: SegySetupType | undefined): boolean => {
  if (setupContent === undefined) {
    return false
  }

  const samples = setupContent.samples
  const headerCoords = setupContent.headerCoords

  return !!(
    setupContent.surveyName &&
    samples?.samplesFormat &&
    samples.scale &&
    (samples.startRecord || samples.startRecord === 0) &&
    headerCoords?.x?.offset &&
    headerCoords.y?.offset &&
    headerCoords.iline?.offset &&
    headerCoords.xline?.offset
  )
}
